import { Box, Divider, LinearProgress, Paper, Stack } from "@mui/material";
import { useEffect } from "react";

import useMyUser from "~/auth/useMyUser";
import SpacedGridContainer from "~/components/SpacedGridContainer";
import SpacedGridItem from "~/components/SpacedGridItem";
import { BodyText, BodyTextSmall, SubtitleText, TitleText } from "~/components/Typographies";
import snackMessages from "~/helpers/snackMessages";
import { useSnacks } from "~/providers/SnackProvider";
import { useGetMyCommissionConfigQuery } from "~/services/endpoints/apiEndpoints";
import { COMMISSIONS_ENTITY_LABEL } from "~admin/commissions/constants";
import { formatRatePercent } from "~admin/commissions/helpers/commissionFormatters";

interface MyCommissionsProps {
	hosted?: boolean;
}

const MyCommissions = ({ hosted = false }: MyCommissionsProps) => {
	const [openSnack] = useSnacks();
	const { isLoading: userLoading, myUser } = useMyUser();
	const userId = myUser?.userId;

	const { data, isError, isLoading } = useGetMyCommissionConfigQuery(userId ? { userId } : { userId: "" }, {
		skip: !userId
	});

	useEffect(() => {
		if (isError) openSnack(snackMessages.loadError(COMMISSIONS_ENTITY_LABEL), "error");
	}, [isError, openSnack]);

	if (userLoading || (userId && isLoading)) return <LinearProgress />;

	const config = data?.commissionConfig;

	return (
		<Paper elevation={hosted ? 0 : 1} sx={{ p: hosted ? 3 : 2 }}>
			<SpacedGridContainer>
				{!hosted && (
					<SpacedGridItem maxCols={1}>
						<TitleText>My Commissions</TitleText>
					</SpacedGridItem>
				)}
				<SpacedGridItem maxCols={1}>
					{!config ? (
						<BodyText color="text.secondary">
							You don't have a commission configuration yet. Contact your manager to set one up.
						</BodyText>
					) : (
						<Stack spacing={2}>
							<Box>
								<BodyTextSmall color="text.secondary">Default rate</BodyTextSmall>
								<SubtitleText>{formatRatePercent(config.defaultRatePercent)}</SubtitleText>
							</Box>
							<Divider />
							<Box>
								<BodyText>Customer overrides</BodyText>
								{config.customerOverrides.length === 0 ? (
									<BodyTextSmall color="text.secondary">No customer overrides</BodyTextSmall>
								) : (
									<Stack divider={<Divider flexItem />} spacing={1} sx={{ mt: 1 }}>
										{config.customerOverrides.map((override) => (
											<Stack
												direction="row"
												key={override.customerId}
												spacing={1}
												sx={{ alignItems: "center", justifyContent: "space-between" }}
											>
												<BodyTextSmall>{override.customer?.name ?? override.customerId}</BodyTextSmall>
												<BodyTextSmall>{formatRatePercent(override.ratePercent)}</BodyTextSmall>
											</Stack>
										))}
									</Stack>
								)}
							</Box>
						</Stack>
					)}
				</SpacedGridItem>
			</SpacedGridContainer>
		</Paper>
	);
};

export default MyCommissions;
